
import { defineComponent, computed, ref, watch, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AxiosResponse } from 'axios'
import { ElForm } from 'element-plus'

import UserLoginWrapper from '@/components/auth-wrappers/UserLoginWrapper.vue'
import InputGroupDark from '@/components/layout/InputGroupDark.vue'
import DoubleScroll from '@/components/layout/DoubleScroll.vue'
import ProximityInfo from '@/components/layout/ProximityInfo.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import JrDateTimePicker from '@/components/jr-parts/JrDateTimePicker.vue'
import ScheduleAnswerMultipleView from '@/components/layout/ScheduleAnswerMultipleView.vue'
import ProximityPriorDiscussionScheduleDecide from '@/components/actions/ProximityPriorDiscussionScheduleDecide.vue'
import ProximityWitnessScheduleDecide from '@/components/actions/ProximityWitnessScheduleDecide.vue'
import BoundaryWitnessScheduleDecide from '@/components/actions/BoundaryWitnessScheduleDecide.vue'
import JrApplicantTitle from '@/components/jr-parts/JrApplicantTitle.vue'
import BoundaryInfo from '@/components/layout/BoundaryInfo.vue'

import { RequestTypes } from '@/constants/enums'
import Logger from '@/services/logger'
import MessageTypeLogic from '@/services/messageTypeLogic'
import { RequestHeaderDetail } from '@/api'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import { MessageTypes } from '@/constants/messageTypes'
import { decideDateTimeRule } from '@/constants/rules'
import { isAfterToday } from '@/services/date'
import { ScheduleDecideForm } from '@/types/forms'
import { addMessageQueue, MESSAGE_TYPE_CONFIRM, MESSAGE_TYPE_TOAST } from '@/plugins/messageQueue'
import MessageService from '@/services/message'

const MODE = Object.freeze({
  /** 事前協議 */
  PROXIMITY_PRIOR_DISCUSSION: 1,
  /** 現地立会 */
  PROXIMITY_WITNESS: 2,
  /** 境界立会・現地立会 */
  BOUNDARY_WITNESS: 3,
})

/**
 * 日程確定
 */
export default defineComponent({
  name: 'ScheduleDecide',
  components: {
    InputGroupDark,
    DoubleScroll,
    UserLoginWrapper,
    ProximityInfo,
    InputLabel,
    JrDateTimePicker,
    ScheduleAnswerMultipleView,
    ProximityPriorDiscussionScheduleDecide,
    ProximityWitnessScheduleDecide,
    BoundaryWitnessScheduleDecide,
    JrApplicantTitle,
    BoundaryInfo,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const requestHeaderId = computed(() => {
      const id = Number(route.params.request_header_id)
      return Number.isNaN(id) ? null : id
    })
    const messageType = computed(() => {
      const type = Number(route.params.message_type)
      return Number.isNaN(type) ? null : type
    })
    const typeName = ref<string>('')
    // 対象のメッセージID
    const messageIds = reactive<number[]>([])
    const requestHeader = ref<RequestHeaderDetail | null>(null)
    // モード
    const mode = ref<number | null>(null)
    // 詳細テーブルID
    const detailId = ref<number | null>(null)

    const formRef = ref<InstanceType<typeof ElForm>>()
    const form = reactive<ScheduleDecideForm>({
      date_time: null,
    })

    // 主処理
    watch(
      () => [requestHeaderId, messageType],
      async () => {
        if (route.name !== 'ScheduleDecide') {
          // 遷移中は何もしない
          return
        }
        messageIds.splice(0)
        mode.value = null
        requestHeader.value = null
        form.date_time = null
        detailId.value = null
        // 申請基本IDチェック
        if (requestHeaderId.value == null || messageType.value == null) {
          Logger.info('URLパラメータが不正です')
          router.push({ name: 'NotFound' })
          return
        }

        const logic = new MessageTypeLogic(messageType.value, requestHeaderId.value)
        if (!logic.isScheduleDecideType()) {
          // 日程確定では無い場合404
          Logger.info('表示できないメッセージ種別です。')
          router.push({ name: 'NotFound' })
          return
        }

        // モード判定
        if (messageType.value === MessageTypes.P_PD_DECIDE_DATE) {
          mode.value = MODE.PROXIMITY_PRIOR_DISCUSSION
        } else if (messageType.value === MessageTypes.P_WS_DECIDE_DATE) {
          mode.value = MODE.PROXIMITY_WITNESS
        } else if (messageType.value === MessageTypes.B_WS_DECIDE_DATE) {
          mode.value = MODE.BOUNDARY_WITNESS
        } else {
          Logger.warn('日程決定種別以外の種別です。')
          router.push({ name: 'NotFound' })
          return
        }

        // 申請基本の取得
        const requestHeaderRequest = new RequestHeaderReadRequest(requestHeaderId.value)
        if (!(await requestHeaderRequest.exec())) {
          return
        }
        const { data: requestHeaderData } = requestHeaderRequest.getResponse() as AxiosResponse<RequestHeaderDetail>
        requestHeader.value = requestHeaderData

        // 日付・詳細IDの保持
        if (mode.value === MODE.PROXIMITY_PRIOR_DISCUSSION) {
          form.date_time = requestHeaderRequest.getProximity().proximity.prior_discussion_schedule_time ?? null
          detailId.value = requestHeaderRequest.getProximity().proximity.proximity_id as number
        } else if (mode.value === MODE.PROXIMITY_WITNESS) {
          form.date_time = requestHeaderRequest.getProximity().proximity.witness_schedule_time ?? null
          detailId.value = requestHeaderRequest.getProximity().proximity.proximity_id as number
        } else if (mode.value === MODE.BOUNDARY_WITNESS) {
          form.date_time = requestHeaderRequest.getBoundary().boundary.witness_schedule_time ?? null
          detailId.value = requestHeaderRequest.getBoundary().boundary.boundary_id as number
        }

        typeName.value = logic.getTypeName()
        // 調整送信種別を取得
        const answerTypes = logic.getAnswerOfDecideTypes()
        for (const type of answerTypes) {
          const answerTypeLogic = new MessageTypeLogic(type, requestHeaderId.value)
          // 調整送信の最終メッセージIDを取得する
          const messageId = await answerTypeLogic.getLastMessageId()
          if (messageId != null) {
            // 存在する場合は追加
            messageIds.push(messageId)
          }
        }
      },
      { immediate: true, deep: true }
    )

    /**
     * 送信後処理
     */
    const sentComplete = () => {
      // トースト
      addMessageQueue(MESSAGE_TYPE_TOAST, MessageService.bind(MessageService.codes.SAVE_COMPLETE, { target: '日程' }))
      // 確認ダイアログ
      addMessageQueue(
        MESSAGE_TYPE_CONFIRM,
        MessageService.codes.CONFIRM_SCHEDULE_DECIDE,
        () => {
          // はい押下時は次のメッセージ送信画面に遷移する
          const logic = new MessageTypeLogic(messageType.value as number, requestHeaderId.value as number)
          // 次工程の種別検索
          const nextType = logic.getNextType()
          if (nextType == null) {
            throw new Error(`次工程のメッセージ種別が見つかりません。 message_type: ${messageType.value}`)
          }
          // 遷移
          router.push({
            name: 'NormalMessageSend',
            params: {
              request_header_id: requestHeaderId.value as number,
              message_type: nextType,
              action: 'send',
            },
          })
        },
        () => {
          // いいえ押下時は詳細画面に戻る
          if (requestHeader.value != null && requestHeader.value.request_type === RequestTypes.PROXIMITY) {
            // 遷移
            router.push({
              name: 'ProximityDetail',
              params: {
                request_header_id: requestHeaderId.value as number,
              },
            })
          } else if (requestHeader.value != null && requestHeader.value.request_type === RequestTypes.BOUNDARY) {
            // 遷移
            router.push({
              name: 'BoundaryDetail',
              params: {
                request_header_id: requestHeaderId.value as number,
              },
            })
          }
        }
      )
    }

    return {
      RequestTypes,
      requestHeader,
      requestHeaderId,
      messageType,
      messageIds,
      detailId,
      typeName,
      mode,
      MODE,
      formRef,
      form,
      rules: decideDateTimeRule,
      isAfterToday,
      sentComplete,
    }
  },
})
